import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ManagerNav from "./managernav";

const LOGO = `${process.env.PUBLIC_URL}/1.png`;

function formatAmount(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "0";
  return parsed.toFixed(0);
}

export default function AiTokenPayment() {
  const navigate = useNavigate();
  const { amount } = useParams();
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);

  const tokenAmount = useMemo(() => formatAmount(amount), [amount]);
  const currentCredits = Number(company?.AICredits || 0);

  useEffect(() => {
    let cancelled = false;

    const loadCompany = async () => {
      try {
        const response = await fetch("https://api.insightos.co.uk/api/company", {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error("Failed to fetch company");
        }

        const data = await response.json();
        if (!cancelled) {
          setCompany(data?.data?.[0] || null);
        }
      } catch (error) {
        if (!cancelled) {
          setCompany(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadCompany();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <ManagerNav />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-sky-50 to-indigo-100 px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl rounded-3xl border border-white/35 bg-white/85 p-8 shadow-[0_16px_40px_rgba(15,23,42,0.16)] backdrop-blur-xl">
          <div className="mb-6 flex items-center gap-3 border-b border-slate-200 pb-4">
            <img src={LOGO} alt="InsightOS" className="h-12 w-12 rounded-lg object-cover" />
            <div>
              <h1 className="text-2xl font-bold text-slate-900">AI Token Payment Invoice</h1>
              <p className="text-sm text-slate-500">Payment completed successfully</p>
            </div>
          </div>

          <div className="mb-8 flex items-center gap-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500">
              <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <p className="text-lg font-semibold text-emerald-700">Payment Confirmed</p>
              <p className="text-sm text-emerald-800">Your AI token purchase has been processed.</p>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500">Tokens Purchased</p>
              <p className="mt-1 text-3xl font-bold text-slate-900">{tokenAmount}</p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500">AI Credits Available</p>
              <p className="mt-1 text-3xl font-bold text-indigo-700">
                {loading ? "..." : currentCredits}
              </p>
            </div>
          </div>

          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-700">
            <p>
              <span className="font-semibold">Company:</span> {company?.CompanyName || "N/A"}
            </p>
            <p className="mt-1">
              <span className="font-semibold">Company Code:</span> {company?.CompanyCode || "N/A"}
            </p>
          </div>

          <div className="mt-8">
            <button
              type="button"
              onClick={() => navigate("/billing")}
              className="inline-flex items-center rounded-xl border border-white/20 bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 px-6 py-3 text-base font-medium text-white shadow-[0_12px_30px_rgba(49,46,129,0.3)] transition hover:from-indigo-800/95 hover:to-indigo-600/95 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2"
            >
              Continue to Billing
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
